import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Card, Table, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  FaPlus,
  FaTrash,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import { useSelector } from 'react-redux';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import * as api from '~/Services/Service';

const ContasIsentasJuridico = () => {
  const empreendimentos = useSelector((state) => state.empreendimentos);

  const [filtroGE, setFiltroGE] = useState(null);
  const [contasIsentas, setContasIsentas] = useState([]);
  const [contasDisponiveis, setContasDisponiveis] = useState([]);
  const [contaParaIncluir, setContaParaIncluir] = useState(null);
  const [linhaSelecionada, setLinhaSelecionada] = useState(null);
  const [loadingLista, setLoadingLista] = useState(false);
  const [loadingDisponiveis, setLoadingDisponiveis] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [itemExclusao, setItemExclusao] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'apelido_conta',
    direction: 'asc',
  });

  const reqSeqLista = useRef(0);
  const reqSeqDisponiveis = useRef(0);

  const opcoesGE = useMemo(() => {
    const sort = empreendimentos && empreendimentos.sort ? empreendimentos.sort : [];
    return sort.map((item) => ({
      value: item.codigo,
      label: `${item.codigo} - ${item.apelido || item.descricao}`,
    }));
  }, [empreendimentos]);

  const opcoesContas = useMemo(
    () =>
      contasDisponiveis.map((item) => ({
        value: item.codigo_conta,
        label: `${item.codigo_conta} - ${item.apelido_conta}`,
        codigoGrupoEmpresa: item.codigo_grupo_empresa,
      })),
    [contasDisponiveis]
  );

  const contasOrdenadas = useMemo(() => {
    if (!sortConfig.key) return contasIsentas;
    const copia = [...contasIsentas];
    copia.sort((a, b) => {
      const va = a[sortConfig.key];
      const vb = b[sortConfig.key];
      if (va == null && vb == null) return 0;
      if (va == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (vb == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (va < vb) return sortConfig.direction === 'asc' ? -1 : 1;
      if (va > vb) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return copia;
  }, [contasIsentas, sortConfig]);

  const carregarIsentas = useCallback(async (codigoGE) => {
    if (!codigoGE) {
      setContasIsentas([]);
      return;
    }
    const seq = reqSeqLista.current + 1;
    reqSeqLista.current = seq;
    setLoadingLista(true);
    try {
      const resp = await api.listarContasIsentasJuridico(codigoGE);
      if (reqSeqLista.current !== seq) return;
      setContasIsentas(resp || []);
      setLinhaSelecionada(null);
    } catch (err) {
      if (reqSeqLista.current !== seq) return;
      toast.error(
        extrairMensagemErro(err) || 'Erro ao carregar contas isentas.'
      );
      setContasIsentas([]);
    } finally {
      if (reqSeqLista.current === seq) setLoadingLista(false);
    }
  }, []);

  const carregarDisponiveis = useCallback(async (codigoGE) => {
    if (!codigoGE) {
      setContasDisponiveis([]);
      return;
    }
    const seq = reqSeqDisponiveis.current + 1;
    reqSeqDisponiveis.current = seq;
    setLoadingDisponiveis(true);
    try {
      const resp = await api.listarContasDisponiveisJuridico(codigoGE);
      if (reqSeqDisponiveis.current !== seq) return;
      setContasDisponiveis(resp || []);
      setContaParaIncluir(null);
    } catch (err) {
      if (reqSeqDisponiveis.current !== seq) return;
      toast.error(
        extrairMensagemErro(err) || 'Erro ao carregar contas disponíveis.'
      );
      setContasDisponiveis([]);
    } finally {
      if (reqSeqDisponiveis.current === seq) setLoadingDisponiveis(false);
    }
  }, []);

  useEffect(() => {
    if (opcoesGE.length === 1 && !filtroGE) {
      setFiltroGE(opcoesGE[0]);
    }
  }, [opcoesGE, filtroGE]);

  useEffect(() => {
    const codigoGE = filtroGE ? filtroGE.value : null;
    carregarIsentas(codigoGE);
    carregarDisponiveis(codigoGE);
  }, [filtroGE, carregarIsentas, carregarDisponiveis]);

  const handleAlterarGE = (opt) => {
    setFiltroGE(opt);
    setLinhaSelecionada(null);
    setContaParaIncluir(null);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  const handleSelecionarLinha = (item) => {
    if (
      linhaSelecionada &&
      linhaSelecionada.codigo_conta === item.codigo_conta
    ) {
      setLinhaSelecionada(null);
    } else {
      setLinhaSelecionada(item);
    }
  };

  const handleIncluir = async () => {
    if (!filtroGE || !contaParaIncluir) return;
    const codigoGrupoEmpresa =
      contaParaIncluir.codigoGrupoEmpresa != null
        ? contaParaIncluir.codigoGrupoEmpresa
        : filtroGE.value;
    setSalvando(true);
    try {
      await api.incluirContaIsentaJuridico({
        codigoConta: contaParaIncluir.value,
        codigoGrupoEmpresa,
      });
      toast.success('Conta incluída na lista de isentas com sucesso.');
      setContaParaIncluir(null);
      await Promise.all([
        carregarIsentas(filtroGE.value),
        carregarDisponiveis(filtroGE.value),
      ]);
    } catch (err) {
      toast.error(
        extrairMensagemErro(err) || 'Erro ao incluir conta isenta.'
      );
    } finally {
      setSalvando(false);
    }
  };

  const handleSolicitarRemocao = () => {
    if (!linhaSelecionada) return;
    setItemExclusao(linhaSelecionada);
  };

  const handleConfirmarRemocao = async (confirmado) => {
    const alvo = itemExclusao;
    setItemExclusao(null);
    if (!confirmado || !alvo || !filtroGE) return;
    const codigoGrupoEmpresa =
      alvo.codigo_grupo_empresa != null
        ? alvo.codigo_grupo_empresa
        : filtroGE.value;
    setSalvando(true);
    try {
      await api.excluirContaIsentaJuridico({
        codigoConta: alvo.codigo_conta,
        codigoGrupoEmpresa,
      });
      toast.success('Conta removida da lista de isentas com sucesso.');
      setLinhaSelecionada(null);
      await Promise.all([
        carregarIsentas(filtroGE.value),
        carregarDisponiveis(filtroGE.value),
      ]);
    } catch (err) {
      toast.error(
        extrairMensagemErro(err) || 'Erro ao remover conta isenta.'
      );
    } finally {
      setSalvando(false);
    }
  };

  const thStyle = { whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };

  const mensagemConfirmacao = itemExclusao
    ? `Confirma remoção da isenção de ${itemExclusao.apelido_conta} (código ${itemExclusao.codigo_conta})?`
    : '';

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Contas Isentas Jurídico" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-end">
            <Col xs={12} md={6} lg={5} className="mb-2 mb-md-0">
              <label className="small mb-1">Empreendimento</label>
              <Select
                isClearable
                placeholder="Selecione um empreendimento..."
                options={opcoesGE}
                value={filtroGE}
                onChange={handleAlterarGE}
                components={{ DropdownIndicator }}
                styles={customStyles}
                noOptionsMessage={() =>
                  'Nenhum empreendimento disponível'
                }
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-end">
            <Col xs={12} md={6} lg={6} className="mb-2 mb-md-0">
              <label className="small mb-1">Conta para incluir</label>
              <Select
                isClearable
                isDisabled={!filtroGE || loadingDisponiveis}
                isLoading={loadingDisponiveis}
                placeholder={
                  filtroGE
                    ? 'Buscar conta...'
                    : 'Selecione um empreendimento primeiro'
                }
                options={opcoesContas}
                value={contaParaIncluir}
                onChange={setContaParaIncluir}
                components={{ DropdownIndicator }}
                styles={customStyles}
                noOptionsMessage={() => 'Nenhuma conta disponível'}
              />
            </Col>
            <Col
              xs={12}
              md={6}
              lg={6}
              className="d-flex justify-content-md-end flex-wrap"
            >
              <Button
                size="sm"
                variant="success"
                className="mr-2 mb-2 mb-md-0"
                onClick={handleIncluir}
                disabled={!contaParaIncluir || salvando}
              >
                <FaPlus className="mr-1" /> Incluir
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={handleSolicitarRemocao}
                disabled={!linhaSelecionada || salvando}
              >
                <FaTrash className="mr-1" /> Remover
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Contas Isentas
            <span
              className="ml-2"
              style={{ fontSize: '0.85rem', opacity: 0.8 }}
            >
              ({contasOrdenadas.length}{' '}
              {contasOrdenadas.length === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {loadingLista && (
            <div className="p-3 text-center">
              <Loading />
            </div>
          )}

          {!loadingLista && !filtroGE && (
            <p className="text-muted text-center my-4 px-3">
              Selecione um empreendimento para visualizar as contas isentas.
            </p>
          )}

          {!loadingLista && filtroGE && contasOrdenadas.length === 0 && (
            <p className="text-muted text-center my-4 px-3">
              Nenhuma conta isenta para este empreendimento.
            </p>
          )}

          {!loadingLista && filtroGE && contasOrdenadas.length > 0 && (
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th
                      style={{ ...thStyle, width: '120px' }}
                      className="text-center"
                      onClick={() => handleSort('codigo_conta')}
                    >
                      Código {renderSortIcon('codigo_conta')}
                    </th>
                    <th
                      style={thStyle}
                      onClick={() => handleSort('apelido_conta')}
                    >
                      Conta {renderSortIcon('apelido_conta')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contasOrdenadas.map((item) => {
                    const selecionada =
                      linhaSelecionada &&
                      linhaSelecionada.codigo_conta === item.codigo_conta;
                    return (
                      <tr
                        key={`${item.codigo_conta}-${
                          item.codigo_grupo_empresa != null
                            ? item.codigo_grupo_empresa
                            : filtroGE.value
                        }`}
                        style={{
                          cursor: 'pointer',
                          fontWeight: selecionada ? 'bold' : 'normal',
                          backgroundColor: selecionada ? '#e8f4fd' : '',
                        }}
                        onClick={() => handleSelecionarLinha(item)}
                      >
                        <td className="text-center">{item.codigo_conta}</td>
                        <td>{item.apelido_conta}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={!!itemExclusao}
        onClose={handleConfirmarRemocao}
        title="Remover isenção"
        message={mensagemConfirmacao}
        variant="danger"
      />
    </Container>
  );
};

export default ContasIsentasJuridico;
